import { useMutation } from '@/helpers/composables/useApollo';
import {
	SetInFollowedListDocument,
	SetInFollowedListMutation,
	SetInFollowedListMutationVariables,
} from '@/components/watchlist/graphql/mutations/SetInFollowedList.mutation';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { toastSuccess, toastError, addToast } from '@/helpers/toast/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useListsCount } from '@/helpers/composables/useListsCount';
import { useI18n } from './useI18n';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

export function useFollowList() {
	const { followedListsCount, followedListsCountRefetch } = useListsCount();
	const { tc, t } = useI18n();

	const { mutate: setInFollowedListMutation } = useMutation<
		SetInFollowedListMutation,
		SetInFollowedListMutationVariables
	>(SetInFollowedListDocument);

	async function toggleFollowList({
		listId,
		isFollowed,
		listName,
	}: {
		listId: MaybeRefOrGetter<string>;
		isFollowed: MaybeRefOrGetter<boolean>;
		listName: MaybeRefOrGetter<string>;
	}) {
		const preventAction = preventNonSignedInAction();

		if (preventAction || !listId) {
			return;
		}

		if (!isFollowed && followedListsCount.value >= 20) {
			return addToast({
				message: t('WEBAPP_FOLLOWLISTLIMITREACHED', { maxFollowedLists: 20 }) as string,
			});
		}

		await setInFollowedList({ listId, isFollowed, listName });
	}

	async function setInFollowedList({
		listId,
		isFollowed,
		listName,
	}: {
		listId: MaybeRefOrGetter<string>;
		isFollowed: MaybeRefOrGetter<boolean>;
		listName: MaybeRefOrGetter<string>;
	}) {
		try {
			const message = isFollowed
				? t('WEBAPP_HASBEENREMOVEDFROMYOURLIBRARY', { listName: toValue(listName) })
				: t('WEBAPP_HASBEENADDEDTOYOURLIBRARY', { listName: toValue(listName) });
			const trackingValue = toValue(isFollowed) ? 0 : 1;

			await setInFollowedListMutation({
				input: { id: toValue(listId), state: !toValue(isFollowed), name: toValue(listName) },
			});

			toastSuccess({ message: message as string });
			followedListsCountRefetch();

			TrackingHelper.trackEvent('userinteraction', {
				action: 'follow_content',
				label: 'list',
				property: toValue(listId),
				value: trackingValue,
			});
		} catch (e) {
			toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
		}
	}

	return {
		followedListsCount,
		toggleFollowList,
		setInFollowedList,
	};
}
